import React from "react";
import { Box, Paper, Typography, alpha, Radio } from "@mui/material";
import { styled } from "@mui/material/styles";
import { brand, gray } from "../../../../theme/themePrimitives";

const BpIcon = styled("span")(({ theme }) => ({
  borderRadius: "50%",
  width: 16,
  height: 16,
  boxShadow:
    "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
  backgroundColor: "#f5f8fa",
  backgroundImage:
    "linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))",
  ".Mui-focusVisible &": {
    outline: "2px auto rgba(19,124,189,.6)",
    outlineOffset: 2,
  },
  "input:hover ~ &": {
    backgroundColor: "#ebf1f5",
  },
  "input:disabled ~ &": {
    boxShadow: "none",
    background: "rgba(206,217,224,.5)",
  },
}));

const BpCheckedIcon = styled(BpIcon)({
  backgroundColor: '#7F56D9',
  backgroundImage:
    "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
  "&::before": {
    display: "block",
    width: 16,
    height: 16,
    backgroundImage: "radial-gradient(#fff,#fff 28%,transparent 32%)",
    content: '""',
  },
  "input:hover ~ &": {
    backgroundColor: '#7F56D9',
  },
});

interface ModeSelectorCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  primaryButtonColor?: string;
}

export const ModeSelectorCard: React.FC<ModeSelectorCardProps> = ({
  title,
  description,
  icon,
  selected,
  onClick,
  primaryButtonColor = brand[600],
}) => {
  return (
    <Paper
      onClick={onClick}
      elevation={0}
      role="button"
      aria-pressed={selected}
      sx={{
        flex: 1,
        cursor: "pointer",
        borderRadius: "12px",
        border: selected ? `2px solid #7F56D9` : `2px solid ${gray[200]}`,
        backgroundColor: selected ? alpha(brand[400], 0.04) : "transparent",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      {/* Left section: Icon and Content */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flex: 1 }}>
        {/* Icon */}
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: '#F4EBFF',
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            "& svg": {
              width: 12,
              height: 12,
            },
            "& svg path": {
              fill: "#7F56D9",
            },
          }}
        >
          {icon}
        </Box>

        {/* Title and Description */}
        <Box>
          <Typography variant="subtitle2" >
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Box>

      {/* Right section: Radio button */}
      <Radio
        checked={selected}
        disableRipple
        color="default"
        checkedIcon={<BpCheckedIcon />}
        icon={<BpIcon />}
        sx={{
          p: 0,
          "&:hover": {
            bgcolor: "transparent",
          },
        }}
      />
    </Paper>
  );
};

